import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';
import './DocTitleInput.css';

/**
 * DocTitleInput — **문서 제목 칸** (디자인 정본 B15, 2026-09-07 신설).
 *
 * ★★★ **B2 `Input` 과 다른 물건이다.** 폼의 칸이면 `Input`, **문서가 이고 나가는 이름**이면 이것.
 *    문서 안에 서기 때문에 **면을 갖지 않고**(종이를 그대로 쓴다) 밑줄 하나로만 말한다 —
 *    사방 테두리를 두르면 인쇄된 종이가 깨진다.
 *
 * ★★ **왜 신설했나** — 같은 일을 하는 칸이 **셋이었고 셋 다 달랐다**(2026-09-07 실측):
 *    결재 기안(밑줄 2px · 16px/700) · 게시판 좁은 판(사방 1px · 13px/400) ·
 *    게시판 넓은 판(높이 46 · 17px/700). 한 사람이 결재를 올리고 게시판에 글을 쓰면
 *    **제목 칸을 세 번 다르게 만났다.** 셋 중 틀린 것은 없다 — 규격이 없었을 뿐이다.
 *
 * ★ **덤으로 고쳐지는 것** — 결재 기안의 밑줄은 `--border-strong` 이라 라이트에서 종이 위
 *   **1.48:1** 이었다(WCAG 1.4.11 은 3:1). `--input-border`(4.76 / 3.07)로 **두 모드 다 넘긴다.**
 *
 * ★ **오류는 색만으로 말하지 않는다** — 밑줄과 **이름표가 함께** 바뀐다.
 */
export type DocTitleSize = 'md' | 'lg';

export interface DocTitleInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /** 칸 위의 이름표. 안 주면 이름표 줄을 안 그린다(문서 머리에서 자리를 아껴야 할 때). */
  label?: ReactNode;
  /** `md` 32 · 16px/700 — 좁은 자리 &nbsp;/&nbsp; `lg` 40 · 18px/700 — 문서 머리. */
  size?: DocTitleSize;
  /** 오류 상태. 밑줄과 이름표가 함께 붉어진다. */
  invalid?: boolean;
  /** 바깥 상자에 붙는 클래스. `className` 은 `<input>` 에 붙는다. */
  wrapperClassName?: string;
}

export const DocTitleInput = forwardRef<HTMLInputElement, DocTitleInputProps>(
  function DocTitleInput(
    { label, size = 'lg', invalid = false, wrapperClassName, className, id, ...rest },
    ref,
  ) {
    const uid = useId();
    const controlId = id ?? `${uid}-doctitle`;

    const wrapCls = [
      'wc-doctitle',
      `wc-doctitle--${size}`,
      invalid ? 'wc-doctitle--invalid' : '',
      wrapperClassName ?? '',
    ].filter(Boolean).join(' ');

    return (
      <span className={wrapCls}>
        {label !== undefined && (
          <label className="wc-doctitle__lb" htmlFor={controlId}>{label}</label>
        )}
        <input
          ref={ref}
          id={controlId}
          type="text"
          className={['wc-doctitle__el', className ?? ''].filter(Boolean).join(' ')}
          aria-invalid={invalid || undefined}
          {...rest}
        />
      </span>
    );
  },
);
